import React from 'react';

import { isFlaggedFile, scanNoteFor } from '../../utils/fileScan';
import confirmDestructive from './confirmDestructive';

/**
 * What the signed-in surfaces say about a file's malware verdict (PR 10e;
 * utils/fileScan): nothing for a clean file; a quiet "Not checked yet" in
 * secondary ink for one the scanner has not read (yet); and for a flagged
 * file one plain line in the warning ink — "Flagged by the malware scan.
 * Download at your own risk." Never the accent, never a banner: a person is
 * told, not stopped.
 *
 * `compact` is the tile's one-line form, beside the chip: "Flagged" or "Not
 * checked yet", the full sentence in its title, and it gives way before the
 * chip's name does (data-types.css).
 */
export default function FileScanNote({ file, compact = false, className = '' }) {
	const note = scanNoteFor(file);
	if (!note) return null;
	const classes = [
		'file-scan',
		`file-scan--${note.tone}`,
		compact ? 'file-scan--compact' : '',
		className,
	].filter(Boolean).join(' ');
	return (
		<span className={classes} title={compact ? note.text : undefined} data-testid="file-scan">
			{compact ? note.short : note.text}
		</span>
	);
}

/**
 * The confirm before a flagged file's original leaves the server: danger
 * styled, Cancel focused (confirmDestructive), resolving true only on the
 * verb. A file that is not flagged resolves true at once and asks nothing.
 *
 *   act 'download' — Download anyway
 *   act 'view'     — View full resolution: View anyway (nothing is saved)
 */
export function confirmFlaggedFile(modalApi, file, { act = 'download' } = {}) {
	if (!isFlaggedFile(file)) return Promise.resolve(true);
	const name = file?.originalName || 'This file';
	const viewing = act === 'view';
	return new Promise((resolve) => {
		confirmDestructive(modalApi, {
			title: viewing ? 'View a flagged file?' : 'Download a flagged file?',
			content: `${name} was flagged by the malware scan. Open it only if you trust where it came from.`,
			okText: viewing ? 'View anyway' : 'Download anyway',
			onOk: () => resolve(true),
			onCancel: () => resolve(false),
		});
	});
}

/**
 * The one confirm before Download all when any of the files is flagged —
 * "2 of these files were flagged by the malware scan." None flagged
 * resolves true at once.
 */
export function confirmFlaggedFiles(modalApi, files) {
	const list = Array.isArray(files) ? files : [];
	const flagged = list.filter(isFlaggedFile).length;
	if (!flagged) return Promise.resolve(true);
	let which;
	if (list.length === 1) which = 'This file was';
	else if (flagged === list.length) which = 'These files were';
	else which = flagged === 1 ? '1 of these files was' : `${flagged} of these files were`;
	const them = flagged === 1 ? 'it' : 'them';
	return new Promise((resolve) => {
		confirmDestructive(modalApi, {
			title: flagged === 1 ? 'Download a flagged file?' : 'Download flagged files?',
			content: `${which} flagged by the malware scan. Open ${them} only if you trust where ${flagged === 1 ? 'it' : 'they'} came from.`,
			okText: 'Download anyway',
			onOk: () => resolve(true),
			onCancel: () => resolve(false),
		});
	});
}
